import * as React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton } from './Button';
import ClickOutside from './ClickOutside';
import { Icon } from './Icon';

interface IMenuItemProps {
  color?: string;
  iName?: string;
  target: string;
  title: string;
  onClick?: () => void;
  testId?: string;
}

const DropDownMenuList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DropDownMenuOptionContainer = styled.ul`
  position: absolute;
  top: 30px;
  right: -100px;
  width: 200px;
  transform: translateX(-45%);
  background-color: rgb(99, 99, 99);
  border-radius: 8px;
  padding: 0.5rem;
  overflow: hidden;
  transition: height 500ms ease;
  z-index: 999;
  list-style: none;
  margin: 0;
`;

const DropDownMenuContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  flex: 1;
`;

const StyledNavLink = styled(NavLink)`
  color: ${(props: { color?: string }): string =>
    props.color ? props.color : 'var(--color-text)'};
  margin-left: 0.5rem;
`;

const ListItem = styled.li`
  align-items: center;
  border-radius: 10px;
  display: flex;
  height: 50px;
  flex-direction: row;
  padding: 0 0.5rem;
  &:hover,
  &:focus {
    outline: none;
    background-color: #525357;
  }
`;

export const DropDownItem = (props: IMenuItemProps): React.ReactElement => {
  const clickHandler = (e: React.MouseEvent): void => {
    if (props.onClick) {
      e.preventDefault();
      props.onClick();
    }
    return;
  };
  return (
    <ListItem role='none'>
      <Icon name={props.iName} />
      <StyledNavLink
        color={props.color}
        to={props.target}
        title={props.title}
        role='menuitem'
        onClick={clickHandler}
        className='dropdown-menu-item'
        data-testid={props.testId}
      >
        {props.title}
      </StyledNavLink>
    </ListItem>
  );
};

interface IDropdownMenuProps {
  children: React.ReactNode;
  icon?: string;
  label?: string;
  style?: React.CSSProperties;
}

export const DropDownMenu = (props: IDropdownMenuProps): React.ReactElement => {
  const [open, setOpen] = React.useState(false);

  const firstChildRef = React.useRef(null);

  React.useEffect((): void => {
    if (firstChildRef.current) {
      firstChildRef.current.focus();
    }
  }, [firstChildRef]);

  const toggleMenu = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };
  const closeMenu = (): void => setOpen(false);

  return (
    <DropDownMenuContainer style={props.style}>
      <ClickOutside onClickOutside={closeMenu}>
        <DropDownMenuList>
          <li aria-label='Settings'>
            <IconButton
              title={props.label}
              icon={props.icon}
              onClick={toggleMenu}
            />
            {open && (
              <DropDownMenuOptionContainer
                ref={firstChildRef}
                id='settings-menu'
                role='menu'
              >
                {props.children}
              </DropDownMenuOptionContainer>
            )}
          </li>
        </DropDownMenuList>
      </ClickOutside>
    </DropDownMenuContainer>
  );
};
